<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import type { AppNotification, WorkItem } from '@/types/domain';
import { errorMessage } from '@/utils/format';
import {
  groupByProject,
  notificationTypeLabel,
  notificationWhen,
  stackRepeats,
  type StackedNotification,
} from '@/utils/notifications';

/**
 * 工作台右栏：「待我拍板」+「最新动态」。
 *
 * 待我拍板：审批和评审是别人被卡住在等的事，不能和普通任务混在队列里
 * 被筛选条件藏掉——这里常驻置顶，不受左栏任何筛选影响。
 *
 * 最新动态：把铃铛里的通知平铺进工作台，但**刻意不含**任务逾期/临期两类——
 * 左边的队列就是它们的实时视图；通知是昨天的快照，队列才是现状，
 * 同一件事出现两次只会让人怀疑哪边是真的。
 */
const props = defineProps<{
  /** 左栏聚合出的全部待办；这里挑出审批与评审 */
  workItems: WorkItem[];
}>();

const router = useRouter();

const decisions = computed(() => props.workItems
  .filter((item) => item.kind === 'approval' || item.kind === 'review')
  .slice(0, 8));
const decisionsTotal = computed(() => props.workItems
  .filter((item) => item.kind === 'approval' || item.kind === 'review').length);

function decisionPath(item: WorkItem): string {
  if (item.projectId !== null && item.projectId !== undefined && item.referenceId !== null) {
    return `/projects/${item.projectId}/activities?nodeId=${encodeURIComponent(String(item.referenceId))}&edit=1&focus=review`;
  }
  return '/my-work';
}

/* ---------- 最新动态 ---------- */

/** 队列里已经实时呈现的类型不再重复出现 */
const QUEUE_DUPLICATE_TYPES = new Set(['task_overdue', 'task_due_soon']);

const noticesLoading = ref(false);
const notices = ref<AppNotification[]>([]);

async function loadNotices(): Promise<void> {
  noticesLoading.value = true;
  try {
    const payload = await eqsApi.notifications(true, 50);
    notices.value = payload.items.filter((item) => !QUEUE_DUPLICATE_TYPES.has(item.type));
  } catch {
    notices.value = [];
  } finally {
    noticesLoading.value = false;
  }
}

onMounted(loadNotices);

const noticeGroups = computed(() => groupByProject(stackRepeats(notices.value)));
const noticeCount = computed(() => notices.value.length);

async function openNotice(item: StackedNotification): Promise<void> {
  try {
    await eqsApi.markNotificationsRead([item.id]);
  } catch (caught) {
    notify('error', '标记已读失败', errorMessage(caught));
  }
  if (item.link) await router.push(item.link);
  else await loadNotices();
}

async function markAllNotices(): Promise<void> {
  try {
    const result = await eqsApi.markAllNotificationsRead();
    notify('success', '动态已清空', `共 ${result.updated} 条标为已读。`);
    await loadNotices();
  } catch (caught) {
    notify('error', '操作失败', errorMessage(caught));
  }
}
</script>

<template>
  <aside class="work-side">
    <!-- 待我拍板：别人被卡住在等的事，永远第一屏可见 -->
    <section class="side-card side-decisions">
      <header>
        <div><span>NEEDS MY DECISION</span><h3>待我拍板</h3></div>
        <b v-if="decisionsTotal" class="side-count">{{ decisionsTotal }}</b>
      </header>
      <p v-if="!decisions.length" class="side-empty">没有等我审批或评审的事项。</p>
      <ol v-else class="decision-list">
        <li v-for="item in decisions" :key="item.id">
          <div class="decision-copy">
            <b>{{ item.title }}</b>
            <span>{{ item.projectNo || '—' }}<template v-if="item.dueDate"> · 期限 {{ item.dueDate.slice(0, 10) }}</template></span>
          </div>
          <RouterLink class="queue-action primary" :to="decisionPath(item)">
            {{ item.kind === 'review' ? '去评审' : '去审批' }}
          </RouterLink>
        </li>
      </ol>
      <footer v-if="decisionsTotal > decisions.length" class="side-more">
        还有 {{ decisionsTotal - decisions.length }} 条，处理完上面的会顶上来。
      </footer>
    </section>

    <!-- 最新动态：通知流；任务逾期/临期不在这里重复出现，左边队列才是它们的现状 -->
    <section class="side-card">
      <header>
        <div><span>WHAT HAPPENED</span><h3>最新动态</h3></div>
        <button v-if="noticeCount" class="inline-button" type="button" @click="markAllNotices">全部已读</button>
      </header>
      <p v-if="noticesLoading && !noticeCount" class="side-empty">正在读取…</p>
      <p v-else-if="!noticeCount" class="side-empty">
        没有新动态。任务的逾期与到期直接看左边的清单，这里只放需要知道的变化。
      </p>
      <template v-else>
        <div v-for="group in noticeGroups" :key="group.label" class="notice-group">
          <p class="notice-project">{{ group.label }}</p>
          <button
            v-for="item in group.items"
            :key="item.id"
            type="button"
            class="notice-item"
            @click="openNotice(item)"
          >
            <span class="notice-meta">
              <i>{{ notificationTypeLabel(item.type) }}</i>
              <u v-if="item.repeats > 1" class="notice-repeat">已连续提醒 {{ item.repeats }} 天</u>
              <time>{{ notificationWhen(item.createdAt) }}</time>
            </span>
            <b>{{ item.title }}</b>
            <small v-if="item.body">{{ item.body }}</small>
          </button>
        </div>
      </template>
    </section>
  </aside>
</template>

<style scoped>
.work-side { display: grid; gap: 14px; align-content: start; }
.side-card { border: 1px solid var(--line); background: var(--white); }
.side-card > header {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 12px 14px; border-bottom: 1px solid var(--line);
}
.side-card > header span { color: var(--blue); font: 800 10px var(--display); letter-spacing: .12em; }
.side-card > header h3 { margin: 2px 0 0; font-size: 15px; }
.side-count {
  min-width: 26px; padding: 2px 8px; border-radius: 999px; text-align: center;
  background: rgba(209, 52, 56, .1); color: var(--risk); font-size: 13px;
}
.side-empty { margin: 0; padding: 14px; color: var(--muted); font-size: 12px; line-height: 1.8; }
.side-more { padding: 8px 14px; border-top: 1px solid var(--line); color: var(--muted); font-size: 12px; }

.side-decisions { border-top: 3px solid var(--blue); }
.decision-list { margin: 0; padding: 0; list-style: none; }
.decision-list li {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; border-bottom: 1px solid var(--line);
}
.decision-list li:last-child { border-bottom: 0; }
.decision-copy { flex: 1; min-width: 0; display: grid; gap: 2px; }
.decision-copy b { font-size: 13px; color: var(--ink); line-height: 1.5; }
.decision-copy span { color: var(--muted); font-size: 12px; }

.notice-group { border-bottom: 1px solid var(--line); }
.notice-group:last-child { border-bottom: 0; }
.notice-project {
  margin: 0; padding: 7px 14px 5px; background: #f3f7fc;
  color: var(--ink); font-size: 12px; font-weight: 800;
}
.notice-item {
  display: grid; gap: 3px; width: 100%; padding: 9px 14px;
  border: 0; border-top: 1px solid rgba(0, 0, 0, .03);
  background: transparent; cursor: pointer; text-align: left;
}
.notice-item:hover { background: rgba(0, 117, 174, .04); }
.notice-meta { display: flex; align-items: baseline; gap: 8px; }
.notice-meta i { color: var(--blue); font-style: normal; font-size: 11px; font-weight: 800; }
.notice-meta time { margin-left: auto; color: var(--muted); font-size: 11px; }
.notice-repeat {
  padding: 0 6px; border-radius: 999px; background: rgba(209, 52, 56, .1);
  color: var(--risk); font-size: 11px; font-weight: 700; text-decoration: none;
}
.notice-item b { color: var(--ink); font-size: 13px; line-height: 1.5; }
.notice-item small { color: var(--muted); font-size: 12px; line-height: 1.6; }
</style>
